const KIND = {
  OFFICIAL: "official",
  NEWS: "news",
  CONVERSATION: "conversation",
};

const SENDER_ROLE = {
  ADMIN: "admin",
  PLAYER: "player",
  OFFICIAL: "official",
};

const META_SK = "META";
const CONTACT_INDEX_PK = "INDEX#CONTACTS";
const OFFICIAL_CATALOG_PK = "OFFICIAL#CATALOG";
const WELCOME_OFFICIAL_ID = "welcome";

const WELCOME_OFFICIAL_BODY = `Bienvenue dans le Royaume de Vocabularium !
Si vous aimez les mots, les nuances qu'ils offrent et la précision qu'un vocabulaire riche apporte à la pensée comme à l'expression, vous êtes ici chez vous.
En effet, Vocabularium est un royaume pour les amoureux de la langue française, où la curiosité devient une quête et les mots, un territoire à conquérir.
Au fil de vos lectures, certains mots accrochent le regard. Un terme inconnu, une expression dont le sens vous échappe, un mot que vous avez déjà croisé sans jamais vraiment savoir ce qu'il signifie. Ne passez pas votre chemin. Capturez-le. À partir de là, ils ne vous échapperont plus : le jeu vous les fera régulièrement retrouver, jusqu'à ce que leur sens vous soit devenu familier.
Mais dans Vocabularium, la découverte n'est que le début. À travers les jeux et les défis, vous entraînez votre mémoire, consolidez vos acquis et faites grandir votre maîtrise de la langue, mot après mot. Au fil des parties, des pierres viennent récompenser vos efforts : on les échange contre une potion, pour un peu d'aide en chemin, ou contre un parchemin, pour qu'un pouvoir tienne jusqu'au dernier mot.
Pour vous accompagner dans votre progression, les Quêtes vous donnent chaque jour quatre objectifs adaptés à vos performances récentes. À vous de relever le défi, de faire travailler votre mémoire, de poursuivre votre ascension à travers les 12 titres du Royaume, et d'y découvrir parfois une relique au fond d'un coffre.
Mais une ambition plus grande encore vous attend… Le Royaume cherche son Souverrain.
Pour prétendre au trône, il vous faudra capturer 10 000 mots, en retenir durablement au moins 8 000, et franchir les 12 titres qui jalonnent votre ascension.
D'ici là, de belles découvertes vous attendent au fil des pages. Bonne quête, et surtout, bonne découverte !

Royn`;

function asId(value) {
  return String(value || "").trim();
}

function pickDisplayName(...candidates) {
  for (const value of candidates) {
    const name = String(value || "").trim();
    if (name) return name;
  }
  return "";
}

function livingAccountIds(profiles, adminSub) {
  const ids = new Set();
  const admin = asId(adminSub);
  for (const profile of profiles || []) {
    const id = asId(profile?.userId || profile?.playerSub);
    if (id && id !== admin) ids.add(id);
  }
  return ids;
}

function keepIndexedAdminContact(playerSub, livingIds, adminSub) {
  const id = asId(playerSub);
  if (!id || id === asId(adminSub)) return false;
  return livingIds instanceof Set ? livingIds.has(id) : false;
}

function hasUnknownOnlinePlayer(onlineSubs, { contacts, meSub, adminSub } = {}) {
  const known = new Set();
  for (const contact of contacts || []) {
    const player = asId(contact.playerSub);
    const peer = asId(contact.peerSub);
    if (player) known.add(player);
    if (peer) known.add(peer);
  }
  const me = asId(meSub);
  const admin = asId(adminSub);
  return (onlineSubs || []).some((sub) => {
    const id = asId(sub);
    return Boolean(id && id !== me && id !== admin && !known.has(id));
  });
}

function conversationThreadId(playerSub) {
  return `conv:${asId(playerSub)}`;
}

function officialThreadId(playerSub) {
  return `official:${asId(playerSub)}`;
}

function newsThreadId(playerSub) {
  return `news:${asId(playerSub)}`;
}

function announcementChannel(item) {
  if (asId(item?.announcementId || item?.id) === WELCOME_OFFICIAL_ID || item?.kind === "welcome") {
    return "official";
  }
  return item?.channel === "news" ? "news" : "official";
}

function parseThreadId(raw) {
  const value = asId(raw);
  const conv = value.match(/^conv:([A-Za-z0-9_-]+)$/);
  if (conv) {
    return {
      kind: KIND.CONVERSATION,
      playerSub: conv[1],
      threadId: value,
    };
  }
  const official = value.match(/^official:([A-Za-z0-9_-]+)$/);
  if (official) {
    return {
      kind: KIND.OFFICIAL,
      playerSub: official[1],
      threadId: value,
    };
  }
  const news = value.match(/^news:([A-Za-z0-9_-]+)$/);
  if (news) {
    return {
      kind: KIND.NEWS,
      playerSub: news[1],
      threadId: value,
    };
  }
  return null;
}

function threadPk(parsed) {
  if (!parsed?.playerSub || !parsed.kind) return "";
  if (parsed.kind === KIND.OFFICIAL) return `THREAD#official#${parsed.playerSub}`;
  if (parsed.kind === KIND.NEWS) return `THREAD#news#${parsed.playerSub}`;
  return `THREAD#conv#${parsed.playerSub}`;
}

function messageSk(createdAt, messageId) {
  return `MSG#${createdAt}#${asId(messageId)}`;
}

function contactIndexSk(playerSub) {
  return `PLAYER#${asId(playerSub)}`;
}

function officialCatalogSk(announcementId) {
  return asId(announcementId) === WELCOME_OFFICIAL_ID
    ? "ANN#WELCOME"
    : `ANN#${asId(announcementId)}`;
}

function officialPlayerMessageSk(announcementId) {
  return asId(announcementId) === WELCOME_OFFICIAL_ID
    ? "MSG#WELCOME"
    : `MSG#ANN#${asId(announcementId)}`;
}

function publicAnnouncement(item) {
  const id = asId(item?.announcementId || item?.id);
  return {
    id,
    kind: id === WELCOME_OFFICIAL_ID || item?.kind === "welcome" ? "welcome" : "announcement",
    channel: announcementChannel(item),
    body: String(item?.body || ""),
    createdAt: String(item?.createdAt || ""),
    updatedAt: String(item?.updatedAt || item?.createdAt || ""),
  };
}

function parseBlasonIndex(value) {
  if (value == null || value === "") return null;
  const index = Number(value);
  if (!Number.isInteger(index) || index < 0 || index > 500) return null;
  return index;
}

function normalizeBlasonIndex(value) {
  const parsed = parseBlasonIndex(value);
  return parsed == null ? 0 : parsed;
}

function canPostChatMessage({ kind, isAdmin, canSend }) {
  if (kind !== KIND.CONVERSATION) return false;
  if (isAdmin) return true;
  return canSend !== false;
}

function canAccessThread({ parsed, userSub, isAdmin }) {
  if (!parsed?.playerSub) return false;
  if (isAdmin) return true;
  return parsed.playerSub === asId(userSub);
}

function readAllowedWebOrigins(raw = process.env.CORS_ORIGINS) {
  const source = String(raw || "").trim() || "http://localhost:3000,http://localhost:3001";
  return source
    .split(",")
    .map((item) => normalizeOrigin(item))
    .filter(Boolean);
}

function normalizeOrigin(value) {
  const raw = String(value || "").trim().replace(/\/$/, "");
  if (!raw || raw === "null") return "";
  try {
    const url = new URL(raw);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "";
    return url.origin;
  } catch {
    return "";
  }
}

function isAllowedMessagingOrigin(origin, allowedOrigins) {
  const normalized = normalizeOrigin(origin);
  if (!normalized) return false;
  return (allowedOrigins || []).some((item) => normalizeOrigin(item) === normalized);
}

function resolveTypingRelay({ senderSub, isAdmin, threadId, adminSub } = {}) {
  const parsed = parseThreadId(threadId);
  const sender = asId(senderSub);
  const admin = asId(adminSub);
  if (!sender || !parsed || parsed.kind !== KIND.CONVERSATION) return null;
  if (!canAccessThread({ parsed, userSub: sender, isAdmin: isAdmin === true })) return null;
  if (isAdmin) {
    const dest = asId(parsed.playerSub);
    if (!dest || dest === sender) return null;
    return { threadId: parsed.threadId, targetSub: dest };
  }
  if (!admin || admin === sender) return null;
  return { threadId: parsed.threadId, targetSub: admin };
}

function unreadTotalForViewer({ isAdmin, contacts }) {
  const list = Array.isArray(contacts) ? contacts : [];
  return list.reduce((sum, contact) => {
    if (isAdmin) {
      return contact.kind === KIND.CONVERSATION
        ? sum + (Number(contact.unread) || 0)
        : sum;
    }
    return sum + (Number(contact.unread) || 0);
  }, 0);
}

function formatClock(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function isMessageReadByRecipient(message, contact) {
  if (
    !message ||
    message.kind === KIND.OFFICIAL ||
    message.kind === KIND.NEWS ||
    message.senderRole === SENDER_ROLE.OFFICIAL
  ) {
    return false;
  }
  const cursor =
    message.senderRole === SENDER_ROLE.ADMIN
      ? contact?.conversationReadAtPlayer
      : contact?.conversationReadAtAdmin;
  const at = String(message.createdAt || "");
  const readAt = String(cursor || "");
  return Boolean(at && readAt && readAt >= at);
}

function formatMessageStamp(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const datePart = date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return `${datePart} · ${formatClock(iso)}`;
}

function lastReceivedLabel(iso) {
  const clock = formatClock(iso);
  return clock ? `dernier message reçu à ${clock}` : "";
}

function isOfficialWelcomeMessage(item) {
  const id = asId(item?.messageId || item?.id || item?.announcementId);
  if (id === WELCOME_OFFICIAL_ID || item?.sk === "MSG#WELCOME") return true;
  return (
    !item?.announcementId &&
    id !== WELCOME_OFFICIAL_ID &&
    String(item?.body || "") === WELCOME_OFFICIAL_BODY
  );
}

function uniqueThreadMessages(items) {
  const seen = new Set();
  const unique = [];
  for (const item of items || []) {
    const id = asId(item?.messageId || item?.id);
    if (!id && !item?.body) continue;
    const key = isOfficialWelcomeMessage(item) ? WELCOME_OFFICIAL_ID : id;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
  }
  return unique;
}

function publicMessage(item, viewerSub) {
  const senderSub = asId(item?.senderSub);
  const id = asId(item?.messageId || item?.id);
  return {
    id,
    announcementId: asId(item?.announcementId || (item?.kind === KIND.OFFICIAL ? id : "")),
    threadId: asId(item?.threadId),
    kind:
      item?.kind === KIND.OFFICIAL
        ? KIND.OFFICIAL
        : item?.kind === KIND.NEWS
          ? KIND.NEWS
          : KIND.CONVERSATION,
    body: String(item?.body || ""),
    createdAt: String(item?.createdAt || ""),
    updatedAt: String(item?.updatedAt || ""),
    senderSub,
    senderRole: item?.senderRole || SENDER_ROLE.PLAYER,
    mine: senderSub === asId(viewerSub),
  };
}

module.exports = {
  KIND,
  SENDER_ROLE,
  META_SK,
  CONTACT_INDEX_PK,
  OFFICIAL_CATALOG_PK,
  WELCOME_OFFICIAL_ID,
  WELCOME_OFFICIAL_BODY,
  asId,
  pickDisplayName,
  livingAccountIds,
  keepIndexedAdminContact,
  hasUnknownOnlinePlayer,
  conversationThreadId,
  officialThreadId,
  newsThreadId,
  announcementChannel,
  parseThreadId,
  threadPk,
  messageSk,
  contactIndexSk,
  officialCatalogSk,
  officialPlayerMessageSk,
  publicAnnouncement,
  parseBlasonIndex,
  normalizeBlasonIndex,
  canPostChatMessage,
  canAccessThread,
  readAllowedWebOrigins,
  normalizeOrigin,
  isAllowedMessagingOrigin,
  resolveTypingRelay,
  unreadTotalForViewer,
  formatClock,
  isMessageReadByRecipient,
  formatMessageStamp,
  lastReceivedLabel,
  isOfficialWelcomeMessage,
  uniqueThreadMessages,
  publicMessage,
};
